'use client';
import { useParams, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';

import { routes } from '~constants/routes';
import { QUERY_TOKEN_PARAM_NAME } from '~constants/token';
import { useDialogs } from '~entities/dialogs';
import { PaymentReason, PaymentReasonType } from '~features/annals';
import {
	getPaywallReason,
	useNeedsToShowBotPaywall,
	useNeedsToShowNeuronsPaywall,
} from '~features/paywall';
import { useCurrentBot } from '~hooks/useCurrentBot';
import { useSetLastBotId } from '~hooks/useLastBotId';
import { useSession } from '~hooks/useSession';
import { usePathname, useRouter } from '~i18n/navigation';
import { isValidClientBotId } from '~utils/ids';

export const Handler = () => {
	const { isDemoUser, isSuccess, isSafeMode } = useSession();
	const searchParams = useSearchParams();
	const pathname = usePathname();
	const { replace } = useRouter();
	const { recipientId } = useParams<{ recipientId: string }>();

	const hadTokenInitially = useRef(false);

	if (searchParams.get(QUERY_TOKEN_PARAM_NAME)) {
		hadTokenInitially.current = true;
	}

	const setLastBotId = useSetLastBotId();
	const { unavailableBots, isSuccess: dialogsLoaded } = useDialogs(recipientId);
	const { isSafeBot, botSuccess } = useCurrentBot({
		recipientId,
	});

	const { setNeedsToShowBotPaywall } = useNeedsToShowBotPaywall();
	const { setNeedsToShowNeuronsPaywall } = useNeedsToShowNeuronsPaywall();

	const processRoute = useCallback(() => {
		const id = Number(recipientId);

		if (!isValidClientBotId(id)) return routes.explore.home;

		const unavailable = unavailableBots.some((b) => b.clientId === id);

		if (unavailable) {
			return routes.explore.home;
		}

		if (hadTokenInitially.current) {
			// if we appeared on a bot page with token its 99% from landing and we save bot for after email confirm redirect
			setLastBotId(recipientId);
		}
		if (pathname.endsWith('/subscribe')) {
			!isDemoUser &&
				setNeedsToShowBotPaywall({
					botId: `${recipientId}`,
					reason: getPaywallReason(PaymentReason.Deeplink),
					reasonType: PaymentReasonType.Onboarding,
				});
			return routes.chats.dialog(recipientId);
		}
		if (pathname.endsWith('/neurons')) {
			!isDemoUser &&
				setNeedsToShowNeuronsPaywall({
					reason: getPaywallReason(PaymentReason.Deeplink),
					reasonType: PaymentReasonType.Onboarding,
				});
			return routes.chats.dialog(recipientId);
		}
	}, [
		recipientId,
		unavailableBots,
		pathname,
		setLastBotId,
		isDemoUser,
		setNeedsToShowBotPaywall,
		setNeedsToShowNeuronsPaywall,
	]);

	useEffect(() => {
		if (
			!dialogsLoaded ||
			!isSuccess ||
			(recipientId && botSuccess && !isSafeBot && isSafeMode)
		)
			return;
		const path = processRoute();
		if (!path) return;
		if (path === pathname) return;
		setTimeout(() => {
			replace(path);
		}, 1000);
		// setNeedsToShowBotPaywall and setNeedsToShowNeuronsPaywall cause rerender of processRoute, ignore for now
		//eslint-disable-next-line
	}, [dialogsLoaded, pathname, replace, botSuccess, isSafeBot, isSafeMode]);

	return null;
};
